import { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import BirthdayPicker from "./BirthdayPicker";
import AnalyzingModal from "./AnalyzingModal";

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

const EDGE_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/yipay`;
const ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

const generateOrderNo = () => {
  const prefix = "FT";
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000000).toString().padStart(6, "0");
  return `${prefix}${timestamp}${random}`;
};

type FormState = "idle" | "analyzing" | "error";

export default function FortuneForm() {
  const [formState, setFormState] = useState<FormState>("idle");
  const [gender, setGender] = useState<"男" | "女" | "">("");
  const [agreed, setAgreed] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [birthdayValue, setBirthdayValue] = useState("");
  const [birthdayDisplay, setBirthdayDisplay] = useState("");
  const [analyzingInfo, setAnalyzingInfo] = useState<{ name: string; gender: string; birthday: string; amount: number; orderNo: string; promise: Promise<{ pay_url?: string; wechat_link?: string; error?: string }> } | null>(null);
  const [displayAmount, setDisplayAmount] = useState<number>(9.9);

  // 从数据库获取支付金额
  useEffect(() => {
    const fetchPaymentConfig = async () => {
      const { data, error } = await supabase
        .from("payment_config")
        .select("amount")
        .eq("id", 1)
        .maybeSingle();
      
      if (data?.amount && !error) {
        setDisplayAmount(parseFloat(data.amount));
      }
    };
    fetchPaymentConfig();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (!gender) {
      alert("请选择性别");
      return;
    }
    if (!birthdayValue) {
      alert("请选择出生日期");
      return;
    }
    if (!agreed) {
      alert("请同意用户隐私协议");
      return;
    }

    // 直接从表单 input 获取姓名，避免 FormData 问题
    const nameInput = form.querySelector('input[name="name"]') as HTMLInputElement;
    const name = nameInput?.value?.trim() || "";
    console.log("[FortuneForm] 获取到的姓名:", name);

    const orderNo = generateOrderNo();
    const amount = displayAmount; // 使用从数据库获取的金额

    // 先设置状态为分析中，确保弹窗能显示
    setFormState("analyzing");

    // 创建支付请求 Promise（立即创建，不等待）
    const payPromise = (async (): Promise<{ pay_url?: string; wechat_link?: string; error?: string }> => {
      try {
        console.log("[FortuneForm] 开始创建订单，orderNo:", orderNo, "amount:", amount);
        
        // 支付回调、状态轮询都依赖订单行存在，这里必须确保落库成功后再继续。
        const birthdayDateOnly = birthdayValue ? birthdayValue.split(" ")[0] : null;
        const { error: orderInsertError } = await supabase.from("orders").insert({
          order_no: orderNo,
          name: name,
          gender: gender,
          birthday: birthdayDateOnly,
          amount: amount,
          status: "pending",
        });

        if (orderInsertError) {
          console.error("[FortuneForm] 创建订单失败:", orderInsertError);
          throw orderInsertError;
        }

        // 2. 保存到 fortune_submissions（异步，不阻塞）
        supabase.from("fortune_submissions").insert({
          name: name,
          gender,
          birthday: birthdayValue,
        }).then(() => {});

        // 3. 提交到 Readdy 表单（异步，不等待结果）
        const params = new URLSearchParams();
        params.append("name", name);
        params.append("gender", gender);
        params.append("birthday", birthdayValue);
        fetch("https://readdy.ai/api/form/d7hmbdpaljjjkv6jgde0", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params.toString(),
        }).catch(() => {});

        // 4. 发起支付请求（超时 8 秒，确保在动画完成前返回）
        console.log("[FortuneForm] 发起支付请求到:", EDGE_URL);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);
        
        // 获取当前页面 origin，解决快手内置浏览器 origin 不可靠的问题
        const pageOrigin = typeof window !== "undefined" ? window.location.origin : "";
        
        // 从 URL 或 localStorage 获取快手 callback
        const kuaishouCallback = new URLSearchParams(window.location.search).get("kuaishou_callback")
          || localStorage.getItem("kuaishou_callback")
          || "";
        
        const res = await fetch(EDGE_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": ANON_KEY,
          },
          body: JSON.stringify({
            order_no: orderNo,
            amount: amount,
            name: name || "八字测算",
            origin: pageOrigin,
            kuaishou_callback: kuaishouCallback,
          }),
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        console.log("[FortuneForm] 支付接口响应状态:", res.status);
        const result = await res.json();
        console.log("[FortuneForm] 支付接口返回:", result);
        
        // 如果返回了企业微信链接，更新到订单中
        if (result.wechat_link) {
          supabase.from("orders").update({
            wechat_link: result.wechat_link,
          }).eq("order_no", orderNo).then(({ error }) => {
            if (error) console.error("[FortuneForm] 更新订单失败:", error);
          });
          // 同时保存到 localStorage
          localStorage.setItem("wechat_link", result.wechat_link);
        }
        
        return result;
      } catch (err) {
        console.error("[FortuneForm] 支付请求失败:", err);
        return { error: "failed" };
      }
    })();

    // 立即设置 analyzingInfo，确保弹窗显示
    setAnalyzingInfo({
      name: name,
      gender,
      birthday: birthdayDisplay || birthdayValue,
      amount,
      orderNo: orderNo,
      promise: payPromise,
    });
  };

  const handleAnalyzingComplete = () => {
    setFormState("idle");
    setAnalyzingInfo(null);
  };

  const handleAnalyzingError = () => {
    setFormState("error");
    setAnalyzingInfo(null);
  };

  return (
    <>
      {/* 表单卡片 */}
      <div
        className="w-full rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(160deg, #fff9f0 0%, #fdf0d8 100%)",
          border: "2px solid #d4a017",
          boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
        }}
      >
        <form
          data-readdy-form
          id="child-fortune-form"
          onSubmit={handleSubmit}
          className="px-5 py-5 space-y-4"
        >
          {/* 姓名 */}
          <div className="flex items-center" style={{ borderBottom: "1px solid #e8c87a" }}>
            <label
              className="text-sm font-semibold flex-shrink-0 w-20"
              style={{ color: "#8b1a1a", fontFamily: "'Noto Serif SC', serif" }}
            >
              您的姓名:
            </label>
            <input
              type="text"
              name="name"
              required
              placeholder="请输入姓名（汉字）"
              className="flex-1 bg-transparent outline-none py-2.5 text-sm"
              style={{ color: "#4a2000", fontFamily: "'Noto Serif SC', serif" }}
            />
          </div>

          {/* 性别 */}
          <div className="flex items-center" style={{ borderBottom: "1px solid #e8c87a" }}>
            <label
              className="text-sm font-semibold flex-shrink-0 w-20"
              style={{ color: "#8b1a1a", fontFamily: "'Noto Serif SC', serif" }}
            >
              您的性别:
            </label>
            <div className="flex gap-2 py-2">
              {(["男", "女"] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGender(g)}
                  className="px-5 py-1.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
                  style={{
                    background: gender === g
                      ? "linear-gradient(135deg, #c8922a, #e8b84b)"
                      : "rgba(200,146,42,0.12)",
                    color: gender === g ? "#fff" : "#8b5e1a",
                    border: `1.5px solid ${gender === g ? "#c8922a" : "#d4a017"}`,
                    fontFamily: "'Noto Serif SC', serif",
                  }}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          {/* 出生日期 */}
          <div
            className="flex items-center cursor-pointer"
            style={{ borderBottom: "1px solid #e8c87a" }}
            onClick={() => setShowPicker(true)}
          >
            <label
              className="text-sm font-semibold flex-shrink-0 w-20 cursor-pointer"
              style={{ color: "#8b1a1a", fontFamily: "'Noto Serif SC', serif" }}
            >
              出生日期:
            </label>
            <div
              className="flex-1 py-2.5 text-sm"
              style={{
                color: birthdayDisplay ? "#4a2000" : "#bbb",
                fontFamily: "'Noto Serif SC', serif",
                fontSize: birthdayDisplay ? "12px" : "14px",
              }}
            >
              {birthdayDisplay || "请选择出生日期"}
            </div>
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-right-s-line" style={{ color: "#c8922a", fontSize: "18px" }} />
            </div>
            <input type="hidden" name="birthday" value={birthdayValue} />
          </div>

          {/* 提交按钮 */}
          <button
            type="submit"
            disabled={formState === "analyzing"}
            className="w-full py-3.5 rounded-xl text-lg font-bold tracking-widest cursor-pointer whitespace-nowrap btn-shimmer"
            style={{
              color: "#5a1a00",
              fontFamily: "'Noto Serif SC', serif",
              letterSpacing: "0.2em",
              border: "none",
              opacity: formState === "analyzing" ? 0.7 : 1,
            }}
          >
            {formState === "analyzing" ? "分析中..." : "立即测算"}
          </button>

          {formState === "error" && (
            <p className="text-center text-sm" style={{ color: "#c0392b" }}>
              提交失败，请稍后重试
            </p>
          )}

          {/* 隐私协议 */}
          <div className="flex items-center justify-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => setAgreed(!agreed)}
              className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer transition-all"
              style={{
                border: `2px solid ${agreed ? "#c8922a" : "#bbb"}`,
                background: agreed ? "#c8922a" : "transparent",
              }}
            >
              {agreed && <i className="ri-check-line text-white" style={{ fontSize: "11px" }} />}
            </button>
            <span className="text-xs" style={{ color: "#8b5e1a" }}>
              同意
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowPrivacy(true); }}
                className="cursor-pointer underline ml-0.5"
                style={{ color: "#c8922a", background: "none", border: "none", fontFamily: "inherit" }}
              >
                《用户隐私协议》
              </button>
            </span>
          </div>
        </form>
      </div>

      {/* 分析动画弹窗 */}
      {formState === "analyzing" && analyzingInfo && (
        <AnalyzingModal
          name={analyzingInfo.name}
          gender={analyzingInfo.gender}
          birthday={analyzingInfo.birthday}
          amount={analyzingInfo.amount}
          orderNo={analyzingInfo.orderNo}
          payPromise={analyzingInfo.promise}
          onComplete={handleAnalyzingComplete}
          onError={handleAnalyzingError}
        />
      )}

      {/* 出生日期选择器 */}
      {showPicker && (
        <BirthdayPicker
          onConfirm={(value, display) => {
            setBirthdayValue(value);
            setBirthdayDisplay(display);
            setShowPicker(false);
          }}
          onCancel={() => setShowPicker(false)}
        />
      )}

      {/* 隐私协议弹窗 */}
      {showPrivacy && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.6)" }}
          onClick={() => setShowPrivacy(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl overflow-hidden"
            style={{ background: "#fff9f0", border: "2px solid #c8922a", maxHeight: "70vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="px-5 py-4 flex items-center justify-between"
              style={{ borderBottom: "1px solid #e8c87a" }}
            >
              <h3 className="font-bold text-base" style={{ color: "#8b1a1a" }}>用户隐私协议</h3>
              <button
                type="button"
                onClick={() => setShowPrivacy(false)}
                className="cursor-pointer w-7 h-7 flex items-center justify-center rounded-full"
                style={{ background: "rgba(200,146,42,0.15)", border: "none" }}
              >
                <i className="ri-close-line" style={{ color: "#8b1a1a" }} />
              </button>
            </div>
            <div
              className="px-5 py-4 overflow-y-auto text-xs space-y-2"
              style={{ color: "#5a3a10", maxHeight: "50vh", lineHeight: "1.8" }}
            >
              <p>尊敬的用户，欢迎阅读本协议：</p>
              <p>本测算服务依据本协议的规定提供服务，本协议具有合同效力。您必须完全同意以下所有条款，才能保证享受到更好的服务。</p>
              <p>用户在申请服务过程中，需要填写一些必要的个人信息，为了更好地为用户服务，请保证提供的这些个人信息的真实、准确、合法、有效。</p>
              <p><strong>保护用户个人信息是我们的基本原则，我们运用各种安全技术和程序建立完善的管理制度来保护用户的个人信息，以免遭受未经授权的访问、使用或披露。</strong></p>
              <p>本测算结果仅供娱乐参考，不构成任何专业建议。</p>
              <p>如有疑问请联系客服，我们将竭诚为您服务。</p>
            </div>
            <div className="px-5 py-4" style={{ borderTop: "1px solid #e8c87a" }}>
              <button
                type="button"
                onClick={() => { setAgreed(true); setShowPrivacy(false); }}
                className="w-full py-2.5 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap"
                style={{
                  background: "linear-gradient(135deg, #c8922a, #e8b84b)",
                  color: "#fff",
                  border: "none",
                  fontFamily: "'Noto Serif SC', serif",
                }}
              >
                同意并继续
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
